import { useState } from "react";

import type { CSSProperties, MouseEventHandler } from "react";

type Props = {
  formId?: string;
  cancelLabel: string;
  saveLabel: string;
  loadingLabel?: string;
  onCancel: MouseEventHandler<HTMLButtonElement>;
  isSaving?: boolean;
  saveDisabled?: boolean;
  className?: string;
  hideCancel?: boolean;
  centered?: boolean;
  /** Text direction. Defaults to reading `document.dir` at render time. */
  dir?: "ltr" | "rtl";
};

// Tokens from Claude Design's "Edit Product Redesign" handoff bundle.
// Save changes uses the `accent` variant overridden to the design's exact
// amber (`#f59e0b` / hover `#d97706`). Discard uses the `ghost` variant.
// Both buttons are rendered as plain elements with inline styles so the
// shipping CSS exactly matches the design — Button variant classes were
// fighting Tailwind's class-precedence rules and producing the wrong
// foreground colour.
const ACCENT = "#f59e0b";
const ACCENT_HOVER = "#d97706";

const BASE_BTN_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.15s",
  fontFamily: "inherit",
  lineHeight: 1.2,
};

// Design's check icon: 16×16, stroke-width 1.6, path M20 6 L9 17 l-5 -5.
function CheckIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

export function FormActionButtons({
  formId,
  cancelLabel,
  saveLabel,
  loadingLabel,
  onCancel,
  isSaving = false,
  saveDisabled = false,
  className,
  hideCancel = false,
  centered = false,
  dir,
}: Props) {
  const direction =
    dir ??
    (typeof document !== "undefined" && document.dir === "rtl" ? "rtl" : "ltr");

  const alignClass = centered
    ? "justify-center"
    : direction === "rtl"
      ? "justify-start"
      : "justify-end";

  const [saveHover, setSaveHover] = useState(false);
  const [cancelHover, setCancelHover] = useState(false);
  const saveDisabledFlag = isSaving || saveDisabled;

  const cancelStyle: CSSProperties = {
    ...BASE_BTN_STYLE,
    background: "transparent",
    color: cancelHover
      ? "var(--text-primary)"
      : "var(--text-secondary)",
    border: "1px solid transparent",
  };

  const saveStyle: CSSProperties = {
    ...BASE_BTN_STYLE,
    background: saveHover && !saveDisabledFlag ? ACCENT_HOVER : ACCENT,
    color: "#fff",
    border: `1px solid ${saveHover && !saveDisabledFlag ? ACCENT_HOVER : ACCENT}`,
    opacity: saveDisabledFlag ? 0.6 : 1,
    cursor: saveDisabledFlag ? "not-allowed" : "pointer",
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-3 ${alignClass} ${className ?? ""}`.trim()}
    >
      {!hideCancel ? (
        <button
          type="button"
          onClick={onCancel}
          style={cancelStyle}
          onMouseEnter={() => setCancelHover(true)}
          onMouseLeave={() => setCancelHover(false)}
        >
          {cancelLabel}
        </button>
      ) : null}
      <button
        type="submit"
        form={formId}
        disabled={saveDisabledFlag}
        style={saveStyle}
        onMouseEnter={() => setSaveHover(true)}
        onMouseLeave={() => setSaveHover(false)}
      >
        {isSaving ? null : <CheckIcon />}
        <span style={{ whiteSpace: "nowrap" }}>
          {isSaving ? loadingLabel ?? saveLabel : saveLabel}
        </span>
      </button>
    </div>
  );
}
